import React, { useEffect, useMemo, useState } from 'react'
import {
  ArrowLeft, CalendarClock, ChevronDown, Loader2, MailCheck, Monitor, Save, Send,
  Smartphone, Sparkles, X
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { apiClient } from '../../config/api'
import {
  apiErrorCode,
  apiErrorMessage,
  communicationsService,
  emailIsValid
} from '../../services/communicationsService'
import type {
  AudienceSource,
  Locale,
  MailLayout,
  NewsletterCampaign,
  NewsletterDraft,
  TemplatePreview,
  TemplateVariable
} from '../../types/communications'
import CampaignDesignPicker from './CampaignDesignPicker'
import MailBlockEditor from './MailBlockEditor'

interface Props {
  campaign: NewsletterCampaign | null
  designs: MailLayout[]
  onClose: () => void
  onSaved: (campaign: NewsletterCampaign, options?: { close?: boolean }) => void
  notify?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

const other = (locale: Locale): Locale => (locale === 'en' ? 'fr' : 'en')

const emptyDraft = (primaryLocale: Locale): NewsletterDraft => ({
  name: '',
  primaryLocale,
  subjectEn: '',
  subjectFr: '',
  blocksEn: [],
  blocksFr: [],
  layoutKey: 'newsletter',
  ctaLabel: '',
  ctaUrl: '',
  preheader: '',
  audience: { sources: ['PORTAL_USERS'], customerIds: [], locales: ['en', 'fr'] }
})

const fromCampaign = (campaign: NewsletterCampaign): NewsletterDraft => ({
  name: campaign.name,
  primaryLocale: campaign.primaryLocale,
  subjectEn: campaign.subjectEn,
  subjectFr: campaign.subjectFr,
  blocksEn: campaign.blocksEn || [],
  blocksFr: campaign.blocksFr || [],
  layoutKey: campaign.layoutKey || 'newsletter',
  ctaLabel: campaign.ctaLabel || '',
  ctaUrl: campaign.ctaUrl || '',
  preheader: campaign.preheader || '',
  audience: {
    sources: [...campaign.audience.sources],
    customerIds: [...campaign.audience.customerIds],
    locales: [...campaign.audience.locales]
  }
})

const CampaignEditor: React.FC<Props> = ({ campaign, designs, onClose, onSaved, notify }) => {
  const { t, i18n } = useTranslation()
  const startLocale: Locale = i18n.language?.startsWith('fr') ? 'fr' : 'en'
  const [draft, setDraft] = useState<NewsletterDraft>(
    campaign ? fromCampaign(campaign) : emptyDraft(startLocale)
  )
  const [saved, setSaved] = useState<NewsletterCampaign | null>(campaign)
  const [dirty, setDirty] = useState(false)
  const [tab, setTab] = useState<Locale>(campaign ? campaign.primaryLocale : startLocale)
  const [customers, setCustomers] = useState<any[]>([])
  const [refine, setRefine] = useState(false)
  const [estimate, setEstimate] = useState<{ count: number; en: number; fr: number } | null>(null)
  const [preview, setPreview] = useState<TemplatePreview | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [mobile, setMobile] = useState(false)
  const [scheduleAt, setScheduleAt] = useState('')
  const [showSchedule, setShowSchedule] = useState(false)
  const [testRecipient, setTestRecipient] = useState('')
  const [busy, setBusy] = useState('')

  const editable = !saved || saved.status === 'DRAFT'
  const secondary = other(draft.primaryLocale)
  const hasSecond = Boolean(
    (secondary === 'fr' ? draft.subjectFr : draft.subjectEn).trim() ||
    (secondary === 'fr' ? draft.blocksFr : draft.blocksEn).length
  )
  const [secondOpen, setSecondOpen] = useState(hasSecond)

  // Campaigns are written freely; these are the only placeholders they can use.
  const variables: TemplateVariable[] = useMemo(() => ([
    { key: 'recipientName', label: t('communications.newsletters.variables.recipientName'), sample: 'Awa Diallo' },
    { key: 'appName', label: t('communications.newsletters.variables.appName'), sample: 'Sini Fleet' },
    { key: 'unsubscribeUrl', label: t('communications.newsletters.variables.unsubscribeUrl'), sample: '' }
  ]), [t])

  useEffect(() => {
    apiClient.get('/customers')
      .then(response => setCustomers(Array.isArray(response.data) ? response.data : response.data?.customers || []))
      .catch(() => setCustomers([]))
  }, [])

  useEffect(() => {
    const scheduled = saved?.scheduledAt ? new Date(saved.scheduledAt) : null
    setScheduleAt(scheduled && !Number.isNaN(scheduled.getTime())
      ? new Date(scheduled.getTime() - scheduled.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
      : '')
  }, [saved?.id, saved?.scheduledAt])

  useEffect(() => {
    if (!draft.audience.sources.length) {
      setEstimate(null)
      return
    }
    const timer = window.setTimeout(() => {
      communicationsService.estimateAudience(draft.audience)
        .then(value => setEstimate({ count: value.count, en: value.byLocale.en, fr: value.byLocale.fr }))
        .catch(() => setEstimate(null))
    }, 350)
    return () => window.clearTimeout(timer)
  }, [JSON.stringify(draft.audience)])

  // Live preview of the tab being edited, debounced like the template editor.
  useEffect(() => {
    const subject = (tab === 'fr' ? draft.subjectFr : draft.subjectEn).trim()
    const blocks = tab === 'fr' ? draft.blocksFr : draft.blocksEn
    const primarySubject = (draft.primaryLocale === 'fr' ? draft.subjectFr : draft.subjectEn).trim()
    const primaryBlocks = draft.primaryLocale === 'fr' ? draft.blocksFr : draft.blocksEn
    if (!primarySubject || !primaryBlocks.length || (tab !== draft.primaryLocale && !subject && !blocks.length)) {
      setPreview(null)
      return
    }
    const timer = window.setTimeout(() => {
      setPreviewing(true)
      communicationsService.previewCampaign({ ...draft, name: draft.name || 'preview' }, tab)
        .then(setPreview)
        .catch(() => setPreview(null))
        .finally(() => setPreviewing(false))
    }, 500)
    return () => window.clearTimeout(timer)
  }, [JSON.stringify(draft), tab])

  const update = <K extends keyof NewsletterDraft>(key: K, value: NewsletterDraft[K]) => {
    setDraft(current => ({ ...current, [key]: value }))
    setDirty(true)
  }

  const problem = () => {
    if (!draft.name.trim()) return t('communications.newsletters.nameRequired')
    if (!(draft.primaryLocale === 'fr' ? draft.subjectFr : draft.subjectEn).trim()) {
      return t('communications.newsletters.subjectRequired')
    }
    if (!(draft.primaryLocale === 'fr' ? draft.blocksFr : draft.blocksEn).length) {
      return t('communications.newsletters.contentRequired')
    }
    if (draft.ctaLabel.trim() && !/^https?:\/\//i.test(draft.ctaUrl.trim())) {
      return t('communications.newsletters.ctaUrlRequired')
    }
    return ''
  }

  const save = async (): Promise<NewsletterCampaign | null> => {
    const issue = problem()
    if (issue) {
      notify?.(issue, 'warning')
      return null
    }
    setBusy('save')
    try {
      const result = saved
        ? await communicationsService.updateCampaign(saved.id, draft)
        : await communicationsService.createCampaign(draft)
      setSaved(result)
      setDraft(fromCampaign(result))
      setDirty(false)
      onSaved(result)
      notify?.(t('communications.newsletters.saved'), 'success')
      return result
    } catch (error) {
      notify?.(apiErrorMessage(error, t('communications.newsletters.saveFailed')), 'error')
      return null
    } finally {
      setBusy('')
    }
  }

  // Sending always works from the stored campaign, so save first if needed.
  const persisted = async () => (dirty || !saved ? save() : saved)

  const sendTest = async () => {
    if (!emailIsValid(testRecipient)) {
      notify?.(t('communications.validation.email'), 'warning')
      return
    }
    const target = await persisted()
    if (!target) return
    setBusy('test')
    try {
      await communicationsService.testCampaign(target.id, testRecipient, tab)
      notify?.(t('communications.newsletters.testSent'), 'success')
    } catch (error) {
      notify?.(apiErrorMessage(error, t('communications.newsletters.testFailed')), 'error')
    } finally {
      setBusy('')
    }
  }

  const queue = async (mode: 'now' | 'schedule') => {
    const target = await persisted()
    if (!target) return
    let when = ''
    if (mode === 'schedule') {
      const date = new Date(scheduleAt)
      if (!scheduleAt || Number.isNaN(date.getTime())) {
        notify?.(t('communications.newsletters.invalidSchedule'), 'warning')
        return
      }
      when = date.toISOString()
    } else if (!window.confirm(t('communications.newsletters.sendNowConfirm', { count: estimate?.count ?? 0 }))) {
      return
    }
    setBusy(mode)
    try {
      const result = mode === 'now'
        ? await communicationsService.sendCampaignNow(target.id)
        : await communicationsService.scheduleCampaign(target.id, when)
      notify?.(t(mode === 'now' ? 'communications.newsletters.sendingCount' : 'communications.newsletters.scheduledCount',
        { count: result.recipientCount }), 'success')
      onSaved(result.campaign, { close: true })
    } catch (error) {
      notify?.(apiErrorCode(error) === 'EMPTY_AUDIENCE'
        ? t('communications.newsletters.emptyAudience')
        : apiErrorMessage(error, t('communications.newsletters.actionFailed')), 'error')
    } finally {
      setBusy('')
    }
  }

  const close = () => {
    if (dirty && !window.confirm(t('communications.newsletters.discardConfirm'))) return
    onClose()
  }

  const toggleSource = (source: AudienceSource) => update('audience', {
    ...draft.audience,
    sources: draft.audience.sources.includes(source)
      ? draft.audience.sources.filter(item => item !== source)
      : [...draft.audience.sources, source]
  })
  const toggleLocale = (item: Locale) => update('audience', {
    ...draft.audience,
    locales: draft.audience.locales.includes(item)
      ? draft.audience.locales.filter(value => value !== item)
      : [...draft.audience.locales, item]
  })
  const toggleCustomer = (id: number) => update('audience', {
    ...draft.audience,
    customerIds: draft.audience.customerIds.includes(id)
      ? draft.audience.customerIds.filter(value => value !== id)
      : [...draft.audience.customerIds, id]
  })

  const field = (locale: Locale) => <>
    <label className="block text-xs font-medium text-gray-600">
      {t('communications.newsletters.subject')}
      <input disabled={!editable} value={locale === 'fr' ? draft.subjectFr : draft.subjectEn}
        onChange={event => update(locale === 'fr' ? 'subjectFr' : 'subjectEn', event.target.value)}
        placeholder={t('communications.newsletters.subjectPlaceholder')}
        className="mt-1.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100" />
    </label>
    <div className="mt-4">
      <div className="text-xs font-medium text-gray-600">{t('communications.newsletters.content')}</div>
      <div className="mt-2">
        <MailBlockEditor
          blocks={locale === 'fr' ? draft.blocksFr : draft.blocksEn}
          variables={editable ? variables : []}
          onChange={blocks => update(locale === 'fr' ? 'blocksFr' : 'blocksEn', blocks)} />
      </div>
    </div>
  </>

  return <div className="space-y-4">
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white p-3">
      <div className="flex items-center gap-3">
        <button onClick={close} className="rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50">
          <ArrowLeft size={14} />
        </button>
        <div>
          <h3 className="text-sm font-semibold">{saved ? saved.name : t('communications.newsletters.new')}</h3>
          <p className="text-[11px] text-gray-500">
            {editable ? t('communications.newsletters.draftHint') : t(`communications.newsletters.status.${saved?.status}`)}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {editable && <button onClick={save} disabled={Boolean(busy)}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold disabled:opacity-50">
          {busy === 'save' ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          {t('communications.newsletters.saveDraft')}
        </button>}
        {editable && <button onClick={() => setShowSchedule(value => !value)}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold">
          <CalendarClock size={13} />{t('communications.newsletters.schedule')}
        </button>}
        {editable && <button onClick={() => queue('now')} disabled={Boolean(busy)}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">
          {busy === 'now' ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
          {t('communications.newsletters.sendNow')}
        </button>}
      </div>
    </div>

    {showSchedule && editable && <div className="flex flex-wrap items-end gap-3 rounded-xl border border-blue-200 bg-blue-50 p-3">
      <label className="text-xs font-medium text-blue-900">
        {t('communications.newsletters.scheduleAt')}
        <input type="datetime-local" value={scheduleAt} onChange={event => setScheduleAt(event.target.value)}
          className="mt-1.5 block rounded-lg border border-blue-200 px-3 py-2 text-sm" />
      </label>
      <button onClick={() => queue('schedule')} disabled={Boolean(busy)}
        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">
        {busy === 'schedule' ? <Loader2 size={13} className="animate-spin" /> : <CalendarClock size={13} />}
        {t('communications.newsletters.scheduleAction')}
      </button>
      <button onClick={() => setShowSchedule(false)} className="rounded-lg p-2 text-blue-700"><X size={14} /></button>
    </div>}

    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_420px]">
      <div className="min-w-0 space-y-4">
        <section className="rounded-xl border border-gray-200 bg-white p-4">
          <label className="block text-xs font-medium text-gray-600">
            {t('communications.newsletters.campaignName')}
            <input disabled={!editable} value={draft.name} onChange={event => update('name', event.target.value)}
              placeholder={t('communications.newsletters.namePlaceholder')}
              className="mt-1.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100" />
          </label>
          <p className="mt-1 text-[10px] text-gray-400">{t('communications.newsletters.nameHint')}</p>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-blue-600" />
            <h4 className="text-sm font-semibold">{t('communications.newsletters.design')}</h4>
          </div>
          <p className="mb-3 mt-1 text-[11px] text-gray-500">{t('communications.newsletters.designHint')}</p>
          <CampaignDesignPicker designs={designs} value={draft.layoutKey} disabled={!editable}
            onChange={key => update('layoutKey', key)} />
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 pb-3">
            <button onClick={() => setTab(draft.primaryLocale)}
              className={`rounded-lg px-3 py-1.5 text-[11px] font-bold uppercase ${
                tab === draft.primaryLocale ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
              }`}>
              {draft.primaryLocale} · {t('communications.newsletters.primaryLanguage')}
            </button>
            {secondOpen
              ? <button onClick={() => setTab(secondary)}
                className={`rounded-lg px-3 py-1.5 text-[11px] font-bold uppercase ${
                  tab === secondary ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                }`}>
                {secondary} · {t('communications.newsletters.optional')}
              </button>
              : editable && <button onClick={() => { setSecondOpen(true); setTab(secondary) }}
                className="rounded-lg border border-dashed border-gray-300 px-3 py-1.5 text-[11px] font-semibold text-gray-600 hover:border-blue-300 hover:text-blue-700">
                + {t('communications.newsletters.addLanguage', { language: secondary.toUpperCase() })}
              </button>}
            {editable && <button
              onClick={() => { update('primaryLocale', secondary); setTab(secondary); setSecondOpen(true) }}
              className="ml-auto text-[10px] font-semibold text-blue-600">
              {t('communications.newsletters.switchPrimary', { language: secondary.toUpperCase() })}
            </button>}
          </div>
          {tab !== draft.primaryLocale && <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
            {t('communications.newsletters.fallbackHint', { language: draft.primaryLocale.toUpperCase() })}
          </p>}
          <div className="mt-4">{field(tab)}</div>
        </section>

        <section className="grid grid-cols-1 gap-3 rounded-xl border border-gray-200 bg-white p-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <h4 className="text-sm font-semibold">{t('communications.newsletters.button')}</h4>
            <p className="mt-1 text-[11px] text-gray-500">{t('communications.newsletters.buttonHint')}</p>
          </div>
          <label className="text-xs font-medium text-gray-600">
            {t('communications.newsletters.buttonLabel')}
            <input disabled={!editable} value={draft.ctaLabel} onChange={event => update('ctaLabel', event.target.value)}
              className="mt-1.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100" />
          </label>
          <label className="text-xs font-medium text-gray-600">
            {t('communications.newsletters.buttonUrl')}
            <input disabled={!editable} value={draft.ctaUrl} onChange={event => update('ctaUrl', event.target.value)}
              placeholder="https://" className="mt-1.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100" />
          </label>
          <label className="text-xs font-medium text-gray-600 sm:col-span-2">
            {t('communications.newsletters.preheader')}
            <input disabled={!editable} value={draft.preheader} onChange={event => update('preheader', event.target.value)}
              className="mt-1.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100" />
            <span className="mt-1 block text-[10px] font-normal text-gray-400">{t('communications.newsletters.preheaderHint')}</span>
          </label>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h4 className="text-sm font-semibold">{t('communications.newsletters.audience')}</h4>
              <p className="text-[11px] text-gray-500">{t('communications.newsletters.approvedHint')}</p>
            </div>
            <div className="rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800">
              {estimate
                ? t('communications.newsletters.estimated', { count: estimate.count, en: estimate.en, fr: estimate.fr })
                : t('communications.newsletters.estimating')}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-gray-700">
              <input type="radio" disabled={!editable} checked={!draft.audience.customerIds.length}
                onChange={() => update('audience', { ...draft.audience, customerIds: [] })}
                className="text-blue-600" />
              {t('communications.newsletters.everyone')}
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-700">
              <input type="radio" disabled={!editable || !customers.length}
                checked={draft.audience.customerIds.length > 0}
                onChange={() => {
                  const first = Number(customers[0]?.id)
                  if (Number.isInteger(first)) update('audience', { ...draft.audience, customerIds: [first] })
                }}
                className="text-blue-600" />
              {t('communications.newsletters.chosenCustomers')}
            </label>
          </div>

          {draft.audience.customerIds.length > 0 && <div className="mt-3 max-h-40 space-y-1 overflow-y-auto rounded-lg border border-gray-200 p-2">
            {customers.map(customer => {
              const id = Number(customer.id)
              return <label key={id} className="flex items-center gap-2 rounded px-1 py-1 text-xs hover:bg-gray-50">
                <input disabled={!editable} type="checkbox" checked={draft.audience.customerIds.includes(id)}
                  onChange={() => toggleCustomer(id)} className="rounded border-gray-300 text-blue-600" />
                <span className="truncate">{customer.company_name || customer.name || `#${id}`}</span>
              </label>
            })}
          </div>}

          <button onClick={() => setRefine(value => !value)}
            className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600">
            <ChevronDown size={12} className={refine ? 'rotate-180 transition' : 'transition'} />
            {t('communications.newsletters.refine')}
          </button>

          {refine && <div className="mt-3 grid grid-cols-1 gap-4 border-t border-gray-100 pt-3 sm:grid-cols-2">
            <div>
              <div className="text-[10px] font-bold uppercase text-gray-400">{t('communications.newsletters.sources')}</div>
              <div className="mt-2 space-y-2">
                {(['PORTAL_USERS', 'CUSTOMER_CONTACTS', 'STAFF_CONTACTS'] as AudienceSource[]).map(source =>
                  <label key={source} className="flex items-start gap-2 text-xs text-gray-700">
                    <input disabled={!editable} type="checkbox" checked={draft.audience.sources.includes(source)}
                      onChange={() => toggleSource(source)} className="mt-0.5 rounded border-gray-300 text-blue-600" />
                    <span>
                      <span className="font-medium">{t(`communications.newsletters.source.${source}`)}</span>
                      {source !== 'PORTAL_USERS' && <span className="block text-[9px] text-gray-400">
                        {t('communications.newsletters.approvedOnly')}
                      </span>}
                    </span>
                  </label>)}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase text-gray-400">{t('communications.newsletters.languages')}</div>
              <div className="mt-2 flex gap-3">
                {(['en', 'fr'] as Locale[]).map(item => <label key={item} className="flex items-center gap-2 text-xs">
                  <input disabled={!editable} type="checkbox" checked={draft.audience.locales.includes(item)}
                    onChange={() => toggleLocale(item)} className="rounded border-gray-300 text-blue-600" />
                  {item.toUpperCase()}
                </label>)}
              </div>
              <p className="mt-2 text-[10px] text-gray-400">{t('communications.newsletters.languagesHint')}</p>
            </div>
          </div>}
        </section>
      </div>

      <div className="space-y-4">
        <section className="rounded-xl border border-gray-200 bg-white p-3">
          <div className="flex items-center justify-between">
            <h4 className="flex items-center gap-2 text-sm font-semibold">
              {t('communications.newsletters.preview')}
              {previewing && <Loader2 size={12} className="animate-spin text-blue-600" />}
            </h4>
            <div className="flex gap-1">
              <button onClick={() => setMobile(false)}
                className={`rounded-md p-2 ${!mobile ? 'bg-blue-50 text-blue-700' : 'text-gray-500'}`}><Monitor size={14} /></button>
              <button onClick={() => setMobile(true)}
                className={`rounded-md p-2 ${mobile ? 'bg-blue-50 text-blue-700' : 'text-gray-500'}`}><Smartphone size={14} /></button>
            </div>
          </div>
          {preview
            ? <div className={`mx-auto mt-3 rounded-lg bg-slate-100 p-3 ${mobile ? 'max-w-[390px]' : ''}`}>
              <div className="mb-2 truncate rounded bg-white px-2 py-1 text-[11px] text-gray-600">{preview.subject}</div>
              <iframe title={t('communications.newsletters.preview')} srcDoc={preview.html} sandbox=""
                className="h-[560px] w-full rounded-lg bg-white shadow-sm" />
            </div>
            : <p className="mt-6 text-center text-xs text-gray-400">{t('communications.newsletters.previewEmpty')}</p>}
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2">
            <MailCheck size={14} className="text-blue-600" />
            <h4 className="text-sm font-semibold">{t('communications.newsletters.test')}</h4>
          </div>
          <div className="mt-3 flex gap-2">
            <input type="email" value={testRecipient} onChange={event => setTestRecipient(event.target.value)}
              placeholder="recipient@example.com"
              className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            <button onClick={sendTest} disabled={Boolean(busy)}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-xs font-medium text-white disabled:opacity-50">
              {busy === 'test' ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              {t('communications.newsletters.send')}
            </button>
          </div>
          <p className="mt-2 text-[10px] text-gray-400">{t('communications.newsletters.testHint')}</p>
        </section>
      </div>
    </div>
  </div>
}

export default CampaignEditor
